import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (creating) await api.post("/admin/categories", editing);
      else await api.patch(`/admin/categories/${editing.id}`, editing);
      toast.success("Saved");
      setEditing(null); setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const move = async (cat, delta) => {
    const idx = cats.findIndex((c) => c.id === cat.id);
    const targetIdx = idx + delta;
    if (targetIdx < 0 || targetIdx >= cats.length) return;
    const other = cats[targetIdx];
    try {
      await Promise.all([
        api.patch(`/admin/categories/${cat.id}`, { order: other.order }),
        api.patch(`/admin/categories/${other.id}`, { order: cat.order }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };
  const del = async (c) => {
    const others = cats.filter((x) => x.id !== c.id);
    const reassignTo = c.tool_count > 0
      ? window.prompt(`'${c.name}' has ${c.tool_count} tool(s). Reassign them to which category? (leave blank for "Other")`, others[0]?.name || "Other")
      : null;
    if (c.tool_count > 0 && reassignTo === null) return;
    try {
      const url = reassignTo ? `/admin/categories/${c.id}?reassign_to=${encodeURIComponent(reassignTo)}` : `/admin/categories/${c.id}`;
      await api.delete(url);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">Tool Categories</h1>
          <p className="text-slate-400 text-sm mt-1">Categories are auto-derived from your existing tools. Rename to cascade across all tools in the category.</p>
        </div>
        <button data-testid="admin-category-create" onClick={() => { setCreating(true); setEditing({ name: "", description: "", icon: "Sparkles", color: "text-purple-300" }); }} className="bx-btn-primary text-sm"><Lucide.Plus size={14} /> Add category</button>
      </div>
      <div className="bx-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left p-3">Order</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-right p-3">Tools</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c, i) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`admin-category-row-${c.slug}`}>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(c, -1)} disabled={i === 0} className="text-slate-500 hover:text-purple-300 disabled:opacity-30"><Lucide.ChevronUp size={14} /></button>
                    <button onClick={() => move(c, 1)} disabled={i === cats.length - 1} className="text-slate-500 hover:text-purple-300 disabled:opacity-30"><Lucide.ChevronDown size={14} /></button>
                    <span className="ml-1 text-xs text-slate-500 font-mono">{c.order}</span>
                  </div>
                </td>
                <td className="p-3 font-semibold">{c.name}</td>
                <td className="p-3 text-slate-400 bx-mono text-xs">{c.slug}</td>
                <td className="p-3 text-right font-mono text-purple-300">{c.tool_count}</td>
                <td className="p-3 text-slate-400 text-xs max-w-xs truncate">{c.description}</td>
                <td className="p-3 text-right">
                  <button data-testid={`admin-category-edit-${c.slug}`} onClick={() => { setCreating(false); setEditing({ ...c }); }} className="bx-btn-ghost text-xs"><Lucide.Pencil size={12} /></button>
                  <button data-testid={`admin-category-delete-${c.slug}`} onClick={() => del(c)} className="bx-btn-ghost text-xs text-rose-300 ml-1"><Lucide.Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {!cats.length && <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5">
          <div className="bx-card p-6 max-w-md w-full">
            <div className="bx-heading text-lg font-bold">{creating ? "Add category" : `Edit ${editing.name}`}</div>
            <div className="mt-3 space-y-3">
              <input data-testid="admin-category-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name (e.g. Marketing)" className="bx-input" />
              <input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Short description (optional)" className="bx-input" />
              <div className="grid grid-cols-2 gap-3">
                <input value={editing.icon || ""} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} placeholder="Lucide icon (e.g. Sparkles)" className="bx-input bx-mono text-xs" />
                <input value={editing.color || ""} onChange={(e) => setEditing({ ...editing, color: e.target.value })} placeholder="Tailwind text-color" className="bx-input bx-mono text-xs" />
              </div>
              {!creating && <div className="text-[11px] text-slate-500"><Lucide.Info size={11} className="inline mr-1" /> Renaming will cascade across <span className="text-purple-300">{editing.tool_count || 0}</span> tools.</div>}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setEditing(null); setCreating(false); }} className="bx-btn-ghost">Cancel</button>
              <button data-testid="admin-category-save" onClick={save} className="bx-btn-primary">{creating ? "Create" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
